/**
 * Top-level storefront sections. Each site maps them to its own root ids through site preferences
 * (catalog category for `onlineShop`/`events`/`courses`, content folder for `restaurants`/`business`),
 * so ids can be localized per site and must never be hard-coded.
 *
 * `business` is a catalog-menu pillar only: it is not persisted as a shopping home
 * (`primary-shopping-category` / `primary-shopping-pillar` cookies) and must not redirect `/`.
 */
export const NAVIGATION_PILLARS = ['onlineShop', 'events', 'courses', 'restaurants', 'business'] as const;

export type NavigationPillar = (typeof NAVIGATION_PILLARS)[number];

export const PERSISTABLE_SHOPPING_PILLARS = ['onlineShop', 'events', 'courses', 'restaurants'] as const;

export type PersistableShoppingPillar = (typeof PERSISTABLE_SHOPPING_PILLARS)[number];

export type NavigationPillarRootIds = Partial<Record<NavigationPillar, string>>;

export function isNavigationPillar(value: unknown): value is NavigationPillar {
    return (NAVIGATION_PILLARS as readonly unknown[]).includes(value);
}

/** Pillars whose path is stored as the shopper's home (cookie + `/` redirect). Excludes `business`. */
export function isPersistableShoppingPillar(value: unknown): value is PersistableShoppingPillar {
    return (PERSISTABLE_SHOPPING_PILLARS as readonly unknown[]).includes(value);
}

/** Drop path/pillar when they must not become the shopping home (currently `business`). */
export function toPersistableShoppingContext(args: {
    navigationPillar: NavigationPillar | undefined;
    primaryCategoryPath: string | null;
}): {
    navigationPillar: PersistableShoppingPillar | undefined;
    primaryCategoryPath: string | null;
} {
    if (args.navigationPillar === 'business') {
        return { navigationPillar: undefined, primaryCategoryPath: null };
    }
    return {
        navigationPillar: isPersistableShoppingPillar(args.navigationPillar) ? args.navigationPillar : undefined,
        primaryCategoryPath: args.primaryCategoryPath,
    };
}

/** Pillar whose configured root category/folder id is `rootId`. */
export function findNavigationPillar(
    rootIds: NavigationPillarRootIds,
    rootId: string | null | undefined
): NavigationPillar | undefined {
    if (!rootId) {
        return undefined;
    }
    return NAVIGATION_PILLARS.find((pillar) => rootIds[pillar] === rootId);
}
